/** How often an open, visible tab asks which Paige is deployed. */
export const checkEvery = 5 * 60_000;

/** Focus and visibility arrive together and in bursts; one ask answers them all for this long. */
export const quietFor = 15_000;

export interface VersionWatchOptions {
  /** This tab's version (deployed-version.ts `ownVersion`). */
  own: string;
  /** The deployed version, or null when it cannot be told. */
  fetchDeployed: () => Promise<string | null>;
  /** Called with the deployed version whenever it differs from `own`. */
  onUpdated: (version: string) => void;
  win?: Window;
  doc?: Document;
  now?: () => number;
}

/**
 * Asks which Paige is deployed when the tab comes back into view or gets focus (the moment someone
 * returns to a tab left open over a deploy), and every five minutes while it is visible; a hidden
 * tab asks nothing until it is looked at again. An answer that differs from this tab's version is
 * handed to `onUpdated`; no answer is not a difference. Returns the way to stop.
 */
export function watchVersion(options: VersionWatchOptions): () => void {
  const win = options.win ?? window;
  const doc = options.doc ?? document;
  const now = options.now ?? Date.now;
  let asking = false;
  let lastAsked = Number.NEGATIVE_INFINITY;
  const check = async (): Promise<void> => {
    if (asking || doc.visibilityState === 'hidden' || now() - lastAsked < quietFor) return;
    asking = true;
    lastAsked = now();
    try {
      const deployed = await options.fetchDeployed();
      if (deployed !== null && deployed !== options.own) options.onUpdated(deployed);
    } finally {
      asking = false;
    }
  };
  const ask = () => void check();
  win.addEventListener('focus', ask);
  doc.addEventListener('visibilitychange', ask);
  const timer = win.setInterval(ask, checkEvery);
  return () => {
    win.removeEventListener('focus', ask);
    doc.removeEventListener('visibilitychange', ask);
    win.clearInterval(timer);
  };
}
